import { defineStore, storeToRefs } from 'pinia'
import { reactive, ref } from 'vue'
import { Session, SessionQuestion } from '@/assets/types'
import { handleSubscription, newSessionQuestion } from './helper'
import { useQuestionnairesStore } from './questionnaires'
import { usePlayerStore } from './player'
import axios from 'axios'
import router from '@/router/router'

export const useSessionsStore = defineStore('sessions', () => {

    const popups = ref({});

    const sessions = reactive({
        current: [] as Session[],

        async init() {
            await this.fetch()
        },

        async fetch() {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions`,
                location.href
            )

            url.searchParams.set("order", 'desc')

            const res = await axios.get(url.toString(), { withCredentials: true })

            if (res.status == 200) {
                data = res.data ?? []
            }

            this.current = data.map(sessionFromAPI)
            return this.current as Session[]
        },

        async fetchOne(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}`,
                location.href
            )

            const res = await axios.get(url.toString(), { withCredentials: true })

            let data = null

            if (res.status === 200) {
                data = sessionFromAPI(res.data)
            }
            return data
        },

        async fetchOneByPin(pin: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/session`,
                location.href
            )

            url.searchParams.set("pinCode", pin)

            const res = await axios.get(url.toString(), { withCredentials: true })

            let data = null

            if (res.status === 200) {
                data = sessionFromAPI(res.data ?? {})
            }
            return data
        },

        async create(s: Session) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions`,
                location.href
            )

            const res = await axios.post(url.toString(), sessionToAPI(s), { withCredentials: true })

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async edit(session: Session) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${session.id}`,
                location.href
            )

            const res = await axios.put(url.toString(), sessionToAPI(session), { withCredentials: true })

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async delete(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}`,
                location.href
            )

            const res = await axios.delete(url.toString(), { withCredentials: true })

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },
    

        sub(id?: string) {
            if (!id) {
                return handleSubscription(
                    'sessions',
                    {
                        create: (doc) => {
                            const session = sessionFromAPI(doc)
                            this.current.unshift(session)
                        },
                        update: (doc) => {
                            const session = sessionFromAPI(doc)
                            const index = this.current.findIndex((q: Session) => q.id === session.id)
                            this.current.splice(index, 1, session)

                            // handle currentSession
                            if (session.id === currentSession.value?.id) {
                                currentSession.value = session
                            }
                        },
                        delete: (doc) => {
                            const index = this.current.findIndex((q: Session) => q.id === doc.id)
                            this.current.splice(index, 1)
                            // handle currentSession
                            if (doc.id === currentSession.value?.id) {
                                currentSession.value = null
                                //redirect to index page
                                router.replace("/")
                            }
                            // handle popup
                            const pop = popups.value[doc.id] as WindowProxy
                            if (pop) {
                                pop.close()
                            }
                        },
                    }
                )
            } else {
                return handleSubscription(
                    `sessions`,
                    {
                        create: () => { },
                        update: (doc) => {
                            if (doc.id !== id) return;
                            const session = sessionFromAPI(doc)
                            const index = this.current.findIndex((q: Session) => q.id === session.id)
                            this.current.splice(index, 1, session)
                            // handle currentSession
                            if (session.id === currentSession.value?.id) {
                                currentSession.value = session
                            }
                        },
                        delete: (doc) => {
                            if (doc.id !== id) return;
                            const index = this.current.findIndex((q: Session) => q.id === doc.id)
                            this.current.splice(index, 1)
                            // handle currentSession
                            if (doc.id === currentSession.value?.id) {
                                currentSession.value = null
                                //redirect to index page
                                router.replace("/")
                            }
                        },
                    }
                )
            }
        },

        async getSessionResults(id: string) {

            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/players`,
                location.href
            )

            url.searchParams.set("order", 'desc')
            url.searchParams.set("order_by", 'score')
            url.searchParams.set("limit", encodeURIComponent(10))
            url.searchParams.set("sessionId", encodeURIComponent(id))

            const res = await axios.get(url.toString(), { withCredentials: true })

            if (res.status == 200) {
                data = res.data ?? []
            }

            return data
        },

        async getSessionStat(sessionId: string) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${sessionId}/statistics`,
                location.href
            )

            const res = await axios.get(url.toString(), { withCredentials: true })
            if (res.status == 200) {
                data = res.data ?? []
            }
            return data
        },

        async getSessionPlayerStat(id: string, playerid: string) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}/statistics/player/${playerid}`,
                location.href
            )
            const res = await axios.get(url.toString(), { withCredentials: true })
            if (res.status == 200) {
                data = res.data ?? []
            }
            return data
        },

        async getPlayerRank(id: string, playerid: string) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}/rank/player/${playerid}`,
                location.href
            )
            const res = await axios.get(url.toString(), { withCredentials: true })
            if (res.status == 200) {
                data = res.data ?? []
            }
            return data
        },

        async submitPresentatorAnswers(id: string, questionIndex: number, answers: number[]) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}/dynamic/answers/${questionIndex}`,
                location.href
            )
            const res = await axios.post(url.toString(), answers, {withCredentials: true})
            if (res.status == 200) {
                data = res.data ?? []
            }
            return data
        },

        async getTokens(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions/${id}/tokens`,
                location.href
            )
            const res = await axios.get(url.toString(), { withCredentials: true })
            return res.data ?? []
        },

        async getSessionQuestionResults(id: string, questionId: number, limit = 10) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/answers`,
                location.href
            )

            url.searchParams.set("order", 'desc')
            url.searchParams.set("order_by", "earnedPoints")
            if (limit > 0) {
                url.searchParams.set("limit", encodeURIComponent(limit))
            }
            url.searchParams.set("sessionId", encodeURIComponent(id))
            url.searchParams.set("questionIndex", encodeURIComponent(questionId))

            const res = await axios.get(url.toString(), { withCredentials: true })

            if (res.status == 200) {
                data = res.data ?? []
            }

            return data
        }
    })

    const currentSession = ref<Session | null>(null)

    async function setCurrentSessionByPin(pin: string) {
        if (!pin) {
            currentSession.value = null
            return null
        }

        return await sessions.fetchOneByPin(pin).then(session => {
            if (session) {
                currentSession.value = session
                sessions.current.push(session)
            } else {
                currentSession.value = null
            }
            return currentSession.value
        })
    }

    async function getStatistics(id: string) {
        if (!id) {
            return null
        }
        return await sessions.getSessionStat(id)
    }

    async function getPlayerStatistics(id: string, playerid: string) {
        if (!id || !playerid) {
            return null
        }
        return await sessions.getSessionPlayerStat(id, playerid)
    }

    async function getPlayerRank(id: string, playerid: string) {
        if (!id || !playerid) {
            return null
        }
        return await sessions.getPlayerRank(id, playerid)
    }

    async function submitPresentatorAnswers(id: string, questionIndex: number, answers: number[]) {
        if (!id || questionIndex == null) {
            return null
        }
        return await sessions.submitPresentatorAnswers(id, questionIndex, answers)
    }

    async function startSession(session: Session) {
        const { questionnaires } = storeToRefs(useQuestionnairesStore())
        const playersStore = usePlayerStore()

        const questionnaire = await questionnaires.value.fetchOne(session.questionnaireId)
        if (!questionnaire || !questionnaire.questions || questionnaire.questions.length === 0) {
            console.error('Cannot start session: Questionnaire not found or empty')
            return
        }

        const { total: playersCount } = await playersStore.getPlayers(session.id)

        if (session.mode === 'treasure') {
            // Treasure mode: no currentQuestion, backend generates tokens on update
            sessions.edit({
                ...session,
                state: 'play',
                currentQuestion: null,
                playersCount: playersCount,
                totalQuestions: questionnaire.questions.length,
            })
            return
        }

        const firstQuestionData = questionnaire.questions[0]
        const initialCurrentQuestion: SessionQuestion = newSessionQuestion(0, firstQuestionData)
        sessions.edit({
            ...session,
            state: 'play',
            currentQuestion: initialCurrentQuestion,
            playersCount: playersCount,
            totalQuestions: questionnaire.questions.length,
        })
    }

    async function continueSession(session: Session) {
        const { questionnaires } = storeToRefs(useQuestionnairesStore())

        // Get the next question from the linked questionnaire or close current question
        if (!session.currentQuestion) {
            return
        }

        const currentQuestion = session.currentQuestion as SessionQuestion
        const questionnaire = await questionnaires.value.fetchOne(session.questionnaireId)

        // If the currentQuestion is not yet answered, close it
        if (currentQuestion.correctAnswers === null) {
            const answers = questionnaire.questions[currentQuestion.index].answers
            currentQuestion.correctAnswers = answers.reduce(
                (acc: number[], a: any, i: number) => a.correct ? [...acc, i] : acc, []
            )

            // Update the currentQuestion in the store
            sessions.edit({ ...session, currentQuestion })
            return
        }

        // Get the next question from the linked questionnaire
        const nextQuestionData = questionnaire.questions.at(currentQuestion.index + 1)
        if (!nextQuestionData) {
            sessions.edit({ ...session, state: 'stop', currentQuestion: null })
            return
        }
        const nextQuestion: SessionQuestion = newSessionQuestion(
            currentQuestion.index + 1,
            nextQuestionData,
        )

        // Update the session state and currentQuestion in the store
        sessions.edit({ ...session, state: 'play', currentQuestion: nextQuestion })
    }


    return {
        popups,
        sessions,
        currentSession,
        setCurrentSessionByPin,
        startSession,
        continueSession,
        getStatistics,
        getPlayerStatistics,
        getPlayerRank,
        submitPresentatorAnswers,
    }
})

function sessionFromAPI(doc: any) {
    return {
        ...doc,
        briefing: doc.briefing,
        mode: doc.mode || 'classic',
        currentQuestion: doc.currentQuestion as SessionQuestion,
    } as Session
}

function sessionToAPI(session: Session) {
    const sess = { ...session,
        briefing: session.briefing ?? "",
        mode: session.mode ?? "classic"
    }
    sess.currentQuestion = sess.currentQuestion
    return sess
}
